import { ChevronDown, ChevronUp, Eye, MoreVertical } from 'lucide-react'
import { useState } from 'react'

type SortDirection = 'asc' | 'desc' | null

type StatusType = 'submitted' | 'new' | 'draft' | 'in-review' | 'accepted' | 'rejected'

interface FilingRecord {
  id: string
  type: string
  company: string
  status: StatusType
  /** When the filing record itself was created. '—' where none exists. */
  createdDate: string
  periodEnd: string
  dueDate: string
  filingDate: string
}

interface Column {
  key: keyof FilingRecord
  label: string
  sortable?: boolean
}

interface FilingHistoryTableProps {
  data: FilingRecord[]
  onView?: (record: FilingRecord) => void
}

const statusStyles: Record<StatusType, { bg: string; text: string }> = {
  submitted: { bg: 'bg-green-50', text: 'text-green-600' },
  new: { bg: 'bg-gray-100', text: 'text-gray-700' },
  draft: { bg: 'bg-yellow-50', text: 'text-orange-600' },
  'in-review': { bg: 'bg-blue-50', text: 'text-blue-600' },
  accepted: { bg: 'bg-emerald-50', text: 'text-emerald-600' },
  rejected: { bg: 'bg-red-50', text: 'text-red-600' },
}

const columns: Column[] = [
  { key: 'type', label: 'Type', sortable: true },
  { key: 'company', label: 'Company', sortable: true },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'createdDate', label: 'Created', sortable: true },
  { key: 'periodEnd', label: 'Period End', sortable: true },
  { key: 'dueDate', label: 'Due Date', sortable: true },
  { key: 'filingDate', label: 'Filing Date', sortable: true },
]

export function FilingHistoryTable({ data, onView }: FilingHistoryTableProps) {
  const [sortColumn, setSortColumn] = useState<keyof FilingRecord | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>(null)

  const handleSort = (columnKey: keyof FilingRecord) => {
    if (sortColumn === columnKey) {
      if (sortDirection === 'asc') {
        setSortDirection('desc')
      } else if (sortDirection === 'desc') {
        setSortDirection(null)
        setSortColumn(null)
      } else {
        setSortDirection('asc')
      }
    } else {
      setSortColumn(columnKey)
      setSortDirection('asc')
    }
  }

  // Date cells are rendered as "04 Aug 2026", which sorts nonsensically as text
  // ("04 Aug" before "31 Jan"). Parse those back to a timestamp; placeholders
  // like '—' / 'N/A' aren't dates and sort last.
  const asTimestamp = (value: string): number | null => {
    if (!/^\d{2} [A-Za-z]{3} \d{4}$/.test(value)) return null
    const parsed = new Date(value).getTime()
    return Number.isNaN(parsed) ? null : parsed
  }

  const sortedData = [...data].sort((a, b) => {
    if (!sortColumn || !sortDirection) return 0

    const aValue = a[sortColumn]
    const bValue = b[sortColumn]

    const aTime = asTimestamp(aValue)
    const bTime = asTimestamp(bValue)
    if (aTime !== null || bTime !== null) {
      if (aTime === null) return 1
      if (bTime === null) return -1
      return sortDirection === 'asc' ? aTime - bTime : bTime - aTime
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
    return 0
  })

  const formatStatus = (status: StatusType) => {
    return status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')
  }

  return (
    <div className='w-full'>
      {/* Mobile: Card View */}
      <div className='md:hidden space-y-3'>
        {sortedData.map((record) => (
          <div
            key={record.id}
            className='bg-white rounded-xl border border-gray-100 p-4 shadow-sm'
          >
            <div className='flex items-start justify-between mb-3'>
              <div>
                <p className='text-sm font-semibold text-gray-900'>{record.type}</p>
                <p className='text-sm text-gray-600 mt-0.5'>{record.company}</p>
              </div>
              <span
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                  statusStyles[record.status]?.bg || 'bg-gray-100'
                } ${statusStyles[record.status]?.text || 'text-gray-700'}`}
              >
                {formatStatus(record.status)}
              </span>
            </div>
            <div className='flex items-start justify-between text-xs text-gray-500 gap-3'>
              <div className='flex flex-wrap gap-x-4 gap-y-1'>
                <span>Created: <span className='text-gray-700 font-medium'>{record.createdDate}</span></span>
                <span>Period End: <span className='text-gray-700 font-medium'>{record.periodEnd}</span></span>
                <span>Due: <span className='text-gray-700 font-medium'>{record.dueDate}</span></span>
                <span>Filed: <span className='text-gray-700 font-medium'>{record.filingDate}</span></span>
              </div>
              <button
                onClick={() => onView?.(record)}
                className='text-gray-700 hover:text-gray-900 transition-colors flex-shrink-0'
                aria-label='View details'
              >
                <Eye className='h-5 w-5' />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop: Table View */}
      <div className='hidden md:block overflow-x-auto'>
        <table className='w-full border-collapse min-w-[980px]'>
          <thead>
            <tr className='bg-gray-50 h-10 max-h-10'>
              {columns.map(column => (
                <th
                  key={column.key}
                  className='px-3 sm:px-6 py-1 text-left h-10 max-h-10 overflow-hidden'
                >
                  <div className='flex items-center gap-2 h-8'>
                    <span className='text-sm font-semibold text-gray-900 truncate'>
                      {column.label}
                    </span>
                    {column.sortable && (
                      <button
                        onClick={() => handleSort(column.key)}
                        className='flex flex-col items-center justify-center hover:opacity-70 transition-opacity flex-shrink-0'
                        aria-label={`Sort by ${column.label}`}
                      >
                        <ChevronUp
                          className={`h-2.5 w-2.5 -mb-0.5 ${
                            sortColumn === column.key && sortDirection === 'asc'
                              ? 'text-gray-900'
                              : 'text-gray-400'
                          }`}
                        />
                        <ChevronDown
                          className={`h-2.5 w-2.5 ${
                            sortColumn === column.key && sortDirection === 'desc'
                              ? 'text-gray-900'
                              : 'text-gray-400'
                          }`}
                        />
                      </button>
                    )}
                    <button
                      className='text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0'
                      aria-label={`${column.label} options`}
                    >
                      <MoreVertical className='h-3 w-3' />
                    </button>
                  </div>
                </th>
              ))}
              <th className='px-3 sm:px-6 py-1 w-16 h-10 max-h-10'></th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((record, index) => (
              <tr
                key={record.id}
                className={`border-t border-gray-100 ${
                  index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                } hover:bg-gray-50 transition-colors`}
              >
                <td className='px-3 sm:px-6 py-4 sm:py-6 text-gray-900 text-start max-w-[120px]'>
                  <div className='truncate whitespace-nowrap' title={record.type}>
                    {record.type}
                  </div>
                </td>
                <td className='px-3 sm:px-6 py-4 sm:py-6 text-gray-900 text-start max-w-[200px]'>
                  <div className='truncate whitespace-nowrap' title={record.company}>
                    {record.company}
                  </div>
                </td>
                <td className='px-3 sm:px-6 py-4 sm:py-6 text-start max-w-[150px]'>
                  <span
                    className={`inline-flex items-center px-6 py-2 rounded-full text-sm font-medium text-start truncate whitespace-nowrap ${
                      statusStyles[record.status]?.bg || 'bg-gray-100'
                    } ${statusStyles[record.status]?.text || 'text-gray-700'}`}
                    title={formatStatus(record.status)}
                  >
                    {formatStatus(record.status)}
                  </span>
                </td>
                <td className='px-3 sm:px-6 py-4 sm:py-6 text-gray-900 text-start max-w-[120px]'>
                  <div className='truncate whitespace-nowrap' title={record.createdDate}>
                    {record.createdDate}
                  </div>
                </td>
                <td className='px-3 sm:px-6 py-4 sm:py-6 text-gray-900 text-start max-w-[150px]'>
                  <div className='truncate whitespace-nowrap' title={record.periodEnd}>
                    {record.periodEnd}
                  </div>
                </td>
                <td className='px-3 sm:px-6 py-4 sm:py-6 text-gray-900 text-start max-w-[120px]'>
                  <div className='truncate whitespace-nowrap' title={record.dueDate}>
                    {record.dueDate}
                  </div>
                </td>
                <td className='px-3 sm:px-6 py-4 sm:py-6 text-gray-900 text-start max-w-[120px]'>
                  <div className='truncate whitespace-nowrap' title={record.filingDate}>
                    {record.filingDate}
                  </div>
                </td>
                <td className='px-3 sm:px-6 py-4 sm:py-6'>
                  <button
                    onClick={() => onView?.(record)}
                    className='text-gray-700 hover:text-gray-900 transition-colors'
                    aria-label='View details'
                  >
                    <Eye className='h-6 w-6' />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
